import { Plugin } from './Plugin';
import { ScenePlugin } from './ScenePlugin';
import { EntityPlugin } from './EntityPlugin';
import { MousePlugin } from './MousePlugin';
import { MoveCursor } from '../objects/MoveCursor';
import { Raycaster, Vector2, Vector3, Euler, Object3D, Quaternion, MeshBasicMaterial } from 'three';
import gsap from 'gsap';

const tmp2 = new Vector2();
const tmp3 = new Vector3();
const sqrt2 = Math.sqrt(2);

export class MovePlugin extends Plugin{
	constructor(manager){
		super(manager);

		this.dependencies.add(MousePlugin);
		this.dependencies.add(ScenePlugin);
		this.dependencies.add(EntityPlugin);

		this.raycaster = new Raycaster();

		this.cursorDamping		= 0.3;

		this.moveCursor 	= new MoveCursor(this.viewer);
		this.cursorTarget 	= this.moveCursor.position.clone();

		this.floors = [];

		this.moveCursorLocked = false;

		this.moving = false;

		this.collisionDistance 	= 0.4;
		this.collisionPosition 	= new Vector3(0, 0.2, 0);
		this.collisionShift 	= new Vector2();
		this.obstacles = [];
		this.rays = [
			// new Vector3(0, 0, 1),
			// new Vector3(1, 0, 1),
			// new Vector3(1, 0, 0),
			// new Vector3(1, 0, -1),
			// new Vector3(0, 0, -1),
			// new Vector3(-1, 0, -1),
			// new Vector3(-1, 0, 0),
			// new Vector3(-1, 0, 1)
	    ];
	}

	get scene(){ return this.manager.get(ScenePlugin).scene; }
	get camera(){ return this.viewer.rendering.camera; }
	get scenePlugin(){ return this.manager.get(ScenePlugin); }
	get entityPlugin(){ return this.manager.get(EntityPlugin); }
	get mouse(){ return this.manager.get(MousePlugin); }

	attach(){
		this.viewer.rendering.ticker.on('tick', this.render);
		this.scenePlugin.on('scene-changed', this.onSceneChange);
		this.scenePlugin.on('scene-updated', this.onSceneChange);
		this.entityPlugin.on('entity-added', this.onEntityAdded);
		this.entityPlugin.on('entity-removed', this.onEntityRemoved);

		this.mouse.on('move', this.viewer.requestRender);
		this.mouse.on('down', this.onMouseDown);
		this.mouse.on('up', this.onMouseUp);
		this.mouse.on('hover', this.onMouseHover);
		this.mouse.on('leave', this.onMouseLeave);
		this.viewer.rendering.scene.add(this.moveCursor);

		this.viewer.domElement.addEventListener('click', this.onClick);
	}

	detach(){
		this.viewer.rendering.ticker.off('tick', this.render);
		this.scenePlugin.off('scene-changed', this.onSceneChange);
		this.scenePlugin.off('scene-updated', this.onSceneChange);
		this.entityPlugin.off('entity-added', this.onEntityAdded);
		this.entityPlugin.off('entity-removed', this.onEntityRemoved);

		this.mouse.off('move', this.viewer.requestRender);
		this.mouse.off('down', this.onMouseDown);
		this.mouse.off('up', this.onMouseUp);
		this.mouse.off('hover', this.onMouseHover);
		this.mouse.off('leave', this.onMouseLeave);
		this.viewer.rendering.scene.remove(this.moveCursor);

		this.viewer.domElement.addEventListener('click', this.onClick);
	}

	onSceneChange = (scene) => {
		const regex = /^SOL*/i;

		this.floors = [];
		this.obstacles = [];
		scene.traverse(obj => {
			if(obj.isMesh){
				if(regex.test(obj.name)){
					this.floors.push(obj);
				}
				else this.obstacles.push(obj);
			}
		});
	}

	onEntityAdded = (entity) => {
		entity.traverse(obj => {
			if(obj.isMesh){
				this.obstacles.push(obj);
			}
		});
	}

	onEntityRemoved = (entity) => {
		entity.traverse(obj => {
			if(obj.isMesh){
				this.obstacles.splice(this.obstacles.indexOf(obj), 1);
			}
		});
	}

	onMouseDown = (e) => {
		this.clickPrevented = false;
	}

	onMouseUp = (e) => {
		this.dragging = false;

		if(tmp2.subVectors(this.mouse.end, this.mouse.start).multiplyScalar(this.viewer.rendering.width/2).length() > 10){
			e.preventDefault();
			this.clickPrevented = true;
		}
	}

	onMouseHover = () => {
		this.mouseCapture = true;
	}

	onMouseLeave = () => {
		this.mouseCapture = false;
		this.moveCursor.hidden = true;
	}

	updateRaycast(){
		this.raycaster.setFromCamera(this.mouse.position, this.camera);
		const intersections = this.raycaster.intersectObject(this.viewer.rendering.scene, true);
		this.intersection 	= ( intersections.length ) > 0 ? intersections[ 0 ] : null;

		if(this.intersection !== null && this.floors.includes(this.intersection.object)){
			this.updateMoveCollision(this.intersection.point);
		}
	}

	onClick = () => {
		if(this.clickPrevented) return;

		if(this.floors.length){
			if(typeof this.intersection != 'undefined' && this.floors.includes(this.intersection.object)){
				this.moveTo();
			}
		}
	}

	updateMoveCursor(){
		const cursorHidden = this.intersection == null
						   || !this.mouseCapture
						   || (!this.floors.includes(this.intersection.object) && this.intersection.object !== this.moveCursor.torus);
		if(this.moveCursor.hidden != cursorHidden){
			this.moveCursor.hidden = cursorHidden;
			if(!cursorHidden){
				this.moveCursor.position.copy(this.collisionPosition);
			}
		}

	}

	updateMoveCollision(point){
		this.collisionPosition.x = point.x;
		this.collisionPosition.z = point.z;
		this.cursorTarget.x = point.x;
		this.cursorTarget.z = point.z;

		
		let distance = +Infinity;
		let collisionPoint = null;

		let collisions;
		this.collisionShift.set(0,0);

		// this.rays.forEach((ray, i) => {
		// 	// We reset the raycaster to this direction
		// 	this.raycaster.set(this.collisionPosition, ray);
		// 	// Test if we intersect with any obstacle mesh
		// 	collisions = this.raycaster.intersectObjects(this.obstacles);
		// 	// And disable that direction if we do
		// 	if (collisions.length > 0 && collisions[0].distance <= this.collisionDistance) {

		// 		if(collisions[0].distance < distance){
		// 			distance = collisions[0].distance;
		// 			collisionPoint = collisions[0].point;
		// 		}
		// 	}
		// });

		// if(collisionPoint){
		// 	tmp3.subVectors(this.collisionPosition, collisionPoint).normalize().multiplyScalar(this.collisionDistance - distance)

		// 	this.cursorTarget.x += tmp3.x;
		// 	this.cursorTarget.z += tmp3.z;

		// }

	}

	moveTo = () => {
		if(this.moveTm) this.moveTm.kill();


		this.moving = true;
		const t = gsap.timeline({
			onUpdate:this.viewer.requestRender
		});
		
		t.set(this, {moveCursorLocked:true}, 0);
		t.add(this.moveCursor.fadeOut(), 0);
		t.to(this.camera.position, {
			duration:1.5,
			x:this.collisionPosition.x,
			z:this.collisionPosition.z,
			ease:'quint.inOut'
		}, 0);
		t.set(this, {moveCursorLocked:false});
		t.add(this.moveCursor.show());
		t.add(() => {
			this.moving = false;
		})

		this.moveTm = t;
		return t;
	}

	moveToPin = (pin) => {
		if(pin && pin.camera && pin.camera.position
		&& (pin.camera.position.x != 0 || pin.camera.position.y != 0 || pin.camera.position.z != 0 )){
			this.moving = true;
			const t = gsap.timeline({
				onUpdate:this.viewer.requestRender
			});
			t.to(this.camera.position, {
				duration:2.5,
				x:pin.camera.position.x,
				y:pin.camera.position.y,
				z:pin.camera.position.z,
				ease:'quint.inOut'
			}, 0);
			t.add(() => {
				this.moving = false;
			})
		}
	}

	render = () => {
		if(this.scene){
			this.updateRaycast();
			this.updateMoveCursor();

			if(this.moveCursor.visible && !this.moveCursorLocked){
				this.moveCursor.position.lerp(this.cursorTarget, this.cursorDamping);
			}


			if(!this.moveCursor.hidden && this.moveCursor.position.distanceTo(this.cursorTarget) > 0.1){
				this.viewer.requestRender()
			}
		}
	}
}